var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
let InputNumber = class InputNumber extends LitElement {
    constructor() {
        super(...arguments);
        this.placeholder = '';
        this.special = false;
        this.value = 0;
        this.size = 'medium';
        this.variant = 'primary';
        this.step = 1;
    }
    handleInput(e) {
        const target = e.target;
        const newValue = parseFloat(target.value);
        if (!isNaN(newValue)) {
            this.value = newValue;
            this.dispatchEvent(new CustomEvent('input-number', {
                detail: this.value,
                bubbles: true,
                composed: true,
            }));
        }
    }
    /**
     * Fires only when the user finishes editing (blur / Enter / spinner click) —
     * native `change` semantics. Use this when the consumer must defer expensive
     * work (validation, cascading state updates) until the value is committed,
     * rather than reacting to every keystroke via `input-number`.
     */
    handleChange(e) {
        const target = e.target;
        const newValue = parseFloat(target.value);
        if (isNaN(newValue))
            return;
        this.value = newValue;
        this.dispatchEvent(new CustomEvent('input-number-change', {
            detail: this.value,
            bubbles: true,
            composed: true,
        }));
    }
    render() {
        const variantClass = `number-input--${this.variant}`;
        const sizeClass = `number-input--${this.size}`;
        const specialClass = this.special ? 'number-input--special' : '';
        return html `
      <input
        class="number-input ${variantClass} ${sizeClass} ${specialClass}"
        type="number"
        .value=${this.value}
        placeholder=${this.placeholder || msg('Number input...', { id: 'form.placeholder.numberInput', desc: 'Number input placeholder' })}
        min=${this.min !== undefined ? this.min : ''}
        max=${this.max !== undefined ? this.max : ''}
        step=${this.step}
        @input=${this.handleInput}
        @change=${this.handleChange}
      />
    `;
    }
};
InputNumber.styles = css `
    .number-input {
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 14px;
      width: 100%;
      box-sizing: border-box;
      transition: border 0.3s, box-shadow 0.3s, background-color 0.3s;
    }

    .number-input:focus {
      border-color: #0078d4;
      box-shadow: 0 0 0 3px rgba(0, 120, 212, 0.2);
      outline: none;
    }

    /* Variants */
    .number-input--primary {
      border-color: #0078d4;
    }

    .number-input--secondary {
      border-color: #6c757d;
    }

    .number-input--success {
      border-color: #28a745;
    }

    .number-input--danger {
      border-color: #dc3545;
    }

    .number-input--warning {
      border-color: #ffc107;
    }

    /* Sizes */
    .number-input--small {
      font-size: 12px;
      padding: 6px 10px;
    }

    .number-input--medium {
      font-size: 14px;
      padding: 8px 12px;
    }

    .number-input--large {
      height: 47px;
      font-size: 20px;
      line-height: 1.5;
      padding: 0 14px;
    }

    /* Special styles */
    .number-input--special {
      background-color: rgba(0, 120, 212, 0.1); /* Default special background */
    }

    .number-input--special.number-input--primary {
      background-color: rgba(0, 120, 212, 0.1);
    }

    .number-input--special.number-input--secondary {
      background-color: rgba(108, 117, 125, 0.1);
    }

    .number-input--special.number-input--success {
      background-color: rgba(40, 167, 69, 0.1);
    }

    .number-input--special.number-input--danger {
      background-color: rgba(220, 53, 69, 0.1);
    }

    .number-input--special.number-input--warning {
      background-color: rgba(255, 193, 7, 0.1);
    }
  `;
__decorate([
    property({ type: String })
], InputNumber.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean })
], InputNumber.prototype, "special", void 0);
__decorate([
    property({ type: Number })
], InputNumber.prototype, "value", void 0);
__decorate([
    property({ type: String })
], InputNumber.prototype, "size", void 0);
__decorate([
    property({ type: String })
], InputNumber.prototype, "variant", void 0);
__decorate([
    property({ type: Number })
], InputNumber.prototype, "min", void 0);
__decorate([
    property({ type: Number })
], InputNumber.prototype, "max", void 0);
__decorate([
    property({ type: Number })
], InputNumber.prototype, "step", void 0);
InputNumber = __decorate([
    localized(),
    customElement('input-number')
], InputNumber);
export { InputNumber };
//# sourceMappingURL=InputNumber.js.map